import React, { useMemo } from 'react';
import { Icon } from '@shakkerai/components';
import { mc } from '@shakkerai/report';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { translate } from '@shakkerai/util-i18n';
import { setTempState, useTempState } from '@starflow/app/store';
import { useModelFavorite } from '../../hooks/data';
import { BASETYPE_NAME_TO_SHORT_LABEL, communityUrl } from '../../constants';
import iconModelUsing from '../../assets/model_using_pic.svg';
import iconCrown from '../../assets/crown_outlined.png';
import { libVipIcon } from '@shakkerai/pro-commercial';
import iconFire from '../../assets/fire.svg';
import { isMainland } from '@shakkerai/pro-utils';

export const ModelCard = React.memo(
  ({ data, using, query, hotBadge, onClick, onUpdate, showTag, showAuthorInfo, width = 175, height = 261, from }) => {
    const { favorited, favoriteModel } = useModelFavorite(data);
    const globaoMode = useTempState('globalMode');

    const handleToggleFavorite = (event) => {
      favoriteModel(favorited ? 0 : 1, 'card');
      onUpdate?.(data, { isLike: favorited ? 0 : 1 });

      !favorited &&
        mc('took.model.collect', {
          model_uuid: data.modelUuid,
          model_version_uuid: data.uuid,
          query,
        });

      event.stopPropagation();
    };

    const handleClick = () => {
      if (data.unsupported) return;

      onClick(data);
      mc('took.model.click', {
        model_uuid: data.modelUuid,
        model_version_uuid: data.uuid,
        query,
      });
    };

    const handleShowImages = (event) => {
      setTempState({
        modelImagesWindowOpened: true,
        modelImagesVersionUuid: data.uuid,
        modelImagesModelName: data.name,
        modelImagesModelUuid: data.modelUuid,
      });
      event.stopPropagation();
    };

    const handleViewModel = () => {
      window.open(`${communityUrl}/modelinfo/${data.modelUuid}?from=tool`);
    };

    const featureTag =
      data.isSelected && data.tagsLabel ? (
        <span className="flex items-center h-5 px-1 bg-black/40 border border-white/50 text-xs text-white rounded">
          {data.tagsLabel}
        </span>
      ) : null;

    const TipTag = useMemo(() => {
      if (!showTag) {
        if (!featureTag) return null;
        return <div className="absolute -top-[26px] left-[12px] z-[5] flex items-center">{featureTag}</div>;
      }

      if (!hotBadge) {
        if (featureTag) {
          return <div className="absolute -top-[26px] left-[12px] z-[5] flex items-center">{featureTag}</div>;
        }

        if (data.isNewlyFavorited) {
          return (
            <div className="absolute -top-[26px] left-[12px] z-[5] flex items-center h-5 px-1 bg-black/40 border border-white/50 rounded">
              <span className="text-white text-xs">{translate('model_recently_favorite')}</span>
            </div>
          );
        }

        if (data.isCheckpointRelated) {
          return (
            <div className="absolute -top-[26px] left-[12px] z-[5] flex items-center h-5 px-1 bg-black/40 border border-white/50 rounded">
              <span className="text-white text-xs">{translate('basemodel_largest_match')}</span>
            </div>
          );
        }

        if (data.isPromptRelated) {
          return (
            <div className="absolute -top-[26px] left-[12px] z-[5] flex items-center h-5 px-1 bg-black/40 border border-white/50 rounded">
              <span className="text-white text-xs">{translate('model_promptbase')}</span>
            </div>
          );
        }
      } else {
        return (
          <div className="absolute -top-[26px] left-[12px] z-[5] flex items-center ">
            <div className="flex items-center h-5 mr-1 px-1 bg-black/40 rounded">
              <img src={iconFire} className="w-4 h-4 mr-0.5" />
              <span className="text-white text-xs font-bold">Top{hotBadge}</span>
            </div>
            {featureTag}
          </div>
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, showTag, hotBadge]);
    return (
      <div style={{ width, height }} className="group relative overflow-hidden bg-[#f4f6f8] rounded">
        <div
          className={`absolute inset-0 z-[5] ${
            using ? 'border-2 border-primary' : 'border border-black/10'
          } rounded pointer-events-none`}
        />
        {globaoMode === 'pro' || from === 'favorite' ? (
          <div className="absolute top-[12px] left-[12px] z-[5] flex items-center h-[18px] px-2 bg-black/40 rounded">
            {data.vipUsed === 1 && (
              <img src={isMainland ? libVipIcon : iconCrown} className="w-2.5 h-2.5 -translate-y-px mr-1" />
            )}
            <span className="text-white text-xs font-bold">
              {BASETYPE_NAME_TO_SHORT_LABEL[data.baseType] || data.baseType}
            </span>
          </div>
        ) : null}
        <img
          onClick={handleClick}
          style={{ height: height - 61 }}
          src={convert2CompressedUrl(data.imageUrl, 350)}
          className="block w-full object-cover cursor-pointer group-hover:scale-110 transition duration-500"
        />
        {data.unsupported && (
          <div
            style={{ height: height - 61 }}
            className="absolute inset-0 z-2 bg-black/80 flex flex-col items-center justify-center"
          >
            <Icon className="icon-zanbukeyong" color="#fff" fontSize={24} />
            <span className="text-white text-xs">{translate('model_online_unsupported')}</span>
          </div>
        )}
        <div className="h-[61px] bg-white" />
        <div className="absolute bottom-0 left-0 z-[2] w-full p-3 bg-white translate-y-[32px] group-hover:translate-y-0 transition duration-500">
          {TipTag}
          <h4 className="flex items-start w-[150px]">
            {using && <img src={iconModelUsing} className="w-[16px] h-[16px] mr-1" />}
            <span className="flex-1 text-xs leading-[17px] font-bold text-[#2d2d2d] text-ellipsis whitespace-nowrap overflow-hidden group-hover:max-h-[34px] group-hover:whitespace-pre-line group-hover:overflow-hidden">
              {data.name}
            </span>
          </h4>
          {showAuthorInfo ? (
            <div className="flex items-center mt-[10px]">
              <img src={convert2CompressedUrl(data.userAvatar, 36)} className="w-[18px] h-[18px] rounded-full" />
              <span className="flex-1 mr-4 ml-1 text-[11px] text-[#7f7f7f] text-ellipsis whitespace-nowrap overflow-hidden">
                {data.userName}
              </span>
              <Icon className="shk-danchuang-yunhangliang" fontSize={12} color="#7f7f7f" />
              <span className="text-[11px] text-[#7F7F7F] ml-[4px]">{data.runCount}</span>
            </div>
          ) : (
            <h5 className="w-[150px] text-xs leading-[18px] text-[#7f7f7f] text-ellipsis whitespace-nowrap overflow-hidden">
              {data.versionName}
            </h5>
          )}
          <div className="flex mt-2 opacity-0 group-hover:opacity-100 transition duration-500 text-xs text-[#2d2d2d]">
            <button
              onClick={handleToggleFavorite}
              className="flex items-center justify-center w-8 h-[26px] bg-[#EDF1F5] rounded"
            >
              <Icon
                color={favorited ? '#FFC351' : '#2d2d2d'}
                className={`${favorited ? 'icon-filled_star' : 'icon-border_star'} -translate-y-px`}
                fontSize={14}
              />
            </button>
            <button
              onClick={handleViewModel}
              className="flex items-center justify-center w-8 h-[26px] ml-2 bg-[#EDF1F5] rounded"
            >
              <Icon className="icon-External_Link" fontSize={14} color="#2d2d2d" />
            </button>
            <button
              className="flex items-center justify-center w-20 h-[26px] ml-2 bg-[#EDF1F5] rounded"
              onClick={handleShowImages}
            >
              <Icon className="shk-moxingku-litu" fontSize={14} color="#2d2d2d" />
              <span style={{ fontSize: '12px' }} className="translate-y-px ml-1 pb-[1px] text-xs text-[#2d2d2d]">
                {translate('model_gallery_btn')}
              </span>
            </button>
          </div>
        </div>
      </div>
    );
  },
);
